import Paper from '@/components/Paper'
import ChartIcons from '@/icons/ChartIcons'
import TrendingDownIcons from '@/icons/TrendingDownIcons'
import TrendingUpIcons from '@/icons/TrendingUpIcons'
import UsageTrendIcons from '@/icons/UsageTrendIcons'
import UserGroupIcons from '@/icons/UserGroupIcons'
import { formatNumberUsage } from '@/utils/function'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import {
    AnalyticsSummary,
    BuiltAnalyticsData,
    DataBuilderConfig,
} from '../../utils/functions'
import ZapIcons from '@/icons/ZapIcons'
import LightBlueIcons from '@/icons/LightBlueIcons'
import TargetIcons from '@/icons/TargetIcons'

interface AnalyticsSummaryProps {
    summary: AnalyticsSummary
    data: BuiltAnalyticsData[]
    config: DataBuilderConfig
}

const TrendIcon: React.FC<{ trend: string; className?: string }> = ({
    trend,
    className = 'w-5 h-5',
}) => {
    switch (trend) {
        case 'increasing':
        case 'growing':
        case 'growth':
            return <TrendingUpIcons className={`${className} text-green-500`} />
        case 'decreasing':
        case 'declining':
        case 'decline':
            return <TrendingDownIcons className={`${className} text-red-500`} />
        default:
            return <TrendingUpIcons className={`${className} text-blue-500`} />
    }
}

const AnalyticsSummaryPaper: React.FC<AnalyticsSummaryProps> = ({
    summary,
    config,
}) => {
    const { t } = useTranslation()

    if (!summary || summary.totalCustomers === 0) {
        return (
            <Paper>
                <Typogaphy className="text-lg font-semibold text-gray-500 text-center">
                    {t('no_data_available_for_analysis')}
                </Typogaphy>
            </Paper>
        )
    }

    return (
        <Paper className="space-y-2">
            <div>
                <div className="flex items-center gap-3 mb-4">
                    <ChartIcons className="w-6 h-6 text-blue-500" />
                    <Typogaphy className="text-xl font-bold text-gray-800">
                        {t('services.analytics_summary')}
                    </Typogaphy>
                    <div className="flex items-center gap-2 ml-auto">
                        <TrendIcon trend={summary.overallTrend} />
                        <span
                            className={`text-sm font-medium ${
                                summary.overallTrend === 'growth'
                                    ? 'text-green-600'
                                    : summary.overallTrend === 'decline'
                                      ? 'text-red-600'
                                      : 'text-blue-600'
                            }`}
                        >
                            {summary.overallTrend.toUpperCase()}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                        <Typogaphy className="text-sm text-blue-600 font-medium">
                            {t('services.analytics.total_customers')}
                        </Typogaphy>
                        <Typogaphy className="text-2xl font-bold text-blue-700">
                            {summary.totalCustomers}
                        </Typogaphy>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                        <Typogaphy className="text-sm text-green-600 font-medium">
                            {t('services.analytics.time_periods')}
                        </Typogaphy>
                        <Typogaphy className="text-2xl font-bold text-green-700">
                            {summary.totalPeriods}
                        </Typogaphy>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                        <Typogaphy className="text-sm text-purple-600 font-medium">
                            {t('services.analytics.best_period')}
                        </Typogaphy>
                        <Typogaphy className="text-lg font-bold text-purple-700">
                            {summary.periodComparison.bestPeriod}
                        </Typogaphy>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                        <Typogaphy className="text-sm text-orange-600 font-medium">
                            {t('services.analytics.avg_growth')}
                        </Typogaphy>
                        <Typogaphy className="text-2xl font-bold text-orange-700">
                            {summary.periodComparison.averageGrowth > 0
                                ? '+'
                                : ''}
                            {summary.periodComparison.averageGrowth.toFixed(1)}%
                        </Typogaphy>
                    </div>
                </div>
            </div>
            <div>
                <div className="flex gap-2">
                    <UsageTrendIcons className="w-6 h-6 text-green-500" />
                    <Typogaphy className="text-lg font-semibold text-gray-800 mb-4">
                        {t('services.analytics.usage_key_trends')}
                    </Typogaphy>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {summary?.usageKeyTrends.slice(0, 6)?.map((trend) => (
                        <div
                            key={trend.usageKey}
                            className="border rounded-lg p-4 hover:shadow-sm transition-shadow"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <Typogaphy className="font-medium text-gray-700">
                                    {t(trend.usageKey)}
                                </Typogaphy>
                                <div className="flex items-center gap-2">
                                    <TrendIcon
                                        trend={trend.trend}
                                        className="w-4 h-4"
                                    />
                                    <span
                                        className={`text-sm font-medium ${
                                            trend.trend === 'increasing'
                                                ? 'text-green-600'
                                                : trend.trend === 'decreasing'
                                                  ? 'text-red-600'
                                                  : trend.trend === 'volatile'
                                                    ? 'text-orange-600'
                                                    : 'text-gray-600'
                                        }`}
                                    >
                                        {trend.changePercentage > 0 ? '+' : ''}
                                        {trend.changePercentage.toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                            <div className="text-sm text-gray-600">
                                <div>
                                    {t('services.analytics.total')}:{' '}
                                    {formatNumberUsage(trend.totalValue)}{' '}
                                    {trend.unit}
                                </div>
                                <div>
                                    {t('services.analytics.peak')}:{' '}
                                    {formatNumberUsage(trend.peakValue)}{' '}
                                    {trend.unit}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div>
                <div className="flex gap-2">
                    <UserGroupIcons className="w-6 h-6 text-primary" />
                    <Typogaphy className="text-lg font-semibold text-gray-800 mb-4">
                        {t('services.analytics.top_customers')}
                    </Typogaphy>
                </div>
                <div className="space-y-3">
                    {summary.topCustomers.slice(0, 5).map((customer, index) => (
                        <div
                            key={customer.customer_id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Typogaphy className="text-sm font-bold text-primary">
                                        {index + 1}
                                    </Typogaphy>
                                </div>
                                <div>
                                    <Typogaphy
                                        className="font-medium text-gray-800"
                                        title={customer.customer_name}
                                    >
                                        {customer.customer_name.length > 40
                                            ? customer.customer_name.substring(
                                                  0,
                                                  40
                                              ) + '...'
                                            : customer.customer_name}
                                    </Typogaphy>
                                    <div className="text-sm text-gray-600">
                                        {t('services.analytics.dominant')}:{' '}
                                        {t(customer.dominantUsageKey)}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="flex items-center gap-2">
                                    <TrendIcon
                                        trend={customer.trend}
                                        className="w-4 h-4"
                                    />
                                    <span
                                        className={`text-sm font-medium ${
                                            customer.trend === 'growing'
                                                ? 'text-green-600'
                                                : customer.trend === 'declining'
                                                  ? 'text-red-600'
                                                  : 'text-gray-600'
                                        }`}
                                    >
                                        {customer.growthRate > 0 ? '+' : ''}
                                        {customer.growthRate.toFixed(1)}%
                                    </span>
                                </div>
                                <div className="text-sm text-gray-500">
                                    Total:{' '}
                                    {formatNumberUsage(customer.totalUsage)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Significant Changes */}
            {summary.significantChanges &&
                summary.significantChanges.length > 0 && (
                    <div>
                        <div className="flex gap-2">
                            <ZapIcons className="w-6 h-6 text-yellow-500" />
                            <Typogaphy className="text-lg font-semibold text-gray-800 mb-4">
                                {t(
                                    'services.analytics.top_customer_data_changes'
                                )}
                            </Typogaphy>
                        </div>
                        <div className="space-y-3">
                            {summary.significantChanges
                                .slice(0, 5)
                                .map((change, index) => (
                                    <div
                                        key={`${change.customer_id}-${change.usageKey}-${index}`}
                                        className="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div
                                                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                    change.changeType ===
                                                    'increase'
                                                        ? 'bg-green-100'
                                                        : 'bg-red-100'
                                                }`}
                                            >
                                                <span
                                                    className={`text-sm font-bold ${
                                                        change.changeType ===
                                                        'increase'
                                                            ? 'text-green-600'
                                                            : 'text-red-600'
                                                    }`}
                                                >
                                                    {index + 1}
                                                </span>
                                            </div>
                                            <div>
                                                <Typogaphy
                                                    className="font-medium text-gray-800"
                                                    title={change.customer_name}
                                                >
                                                    {change.customer_name
                                                        .length > 35
                                                        ? change.customer_name.substring(
                                                              0,
                                                              35
                                                          ) + '...'
                                                        : change.customer_name}
                                                </Typogaphy>
                                                <div className="text-sm text-gray-600">
                                                    {t(change.usageKey)} •{' '}
                                                    {change.previousPeriod} →{' '}
                                                    {change.currentPeriod}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="flex items-center gap-2 mb-1">
                                                <TrendIcon
                                                    trend={
                                                        change.changeType ===
                                                        'increase'
                                                            ? 'increasing'
                                                            : 'decreasing'
                                                    }
                                                    className="w-4 h-4"
                                                />
                                                <span
                                                    className={`text-sm font-bold ${
                                                        change.changeType ===
                                                        'increase'
                                                            ? 'text-green-600'
                                                            : 'text-red-600'
                                                    }`}
                                                >
                                                    {change.percentageChange > 0
                                                        ? '+'
                                                        : ''}
                                                    {change.percentageChange.toFixed(
                                                        1
                                                    )}
                                                    %
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-600">
                                                {formatNumberUsage(
                                                    change.previousValue
                                                )}{' '}
                                                →{' '}
                                                {formatNumberUsage(
                                                    change.currentValue
                                                )}{' '}
                                                {change.unit}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {change.changeType ===
                                                'increase'
                                                    ? '+'
                                                    : ''}
                                                {formatNumberUsage(
                                                    change.absoluteChange
                                                )}{' '}
                                                {change.unit}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                        {summary.significantChanges.length > 5 && (
                            <div className="text-center mt-3">
                                <Typogaphy className="text-sm text-gray-500">
                                    ... {t('services.analytics.and')}{' '}
                                    {summary.significantChanges.length - 5}{' '}
                                    {t(
                                        'services.analytics.more_significant_changes'
                                    )}
                                </Typogaphy>
                            </div>
                        )}
                    </div>
                )}

            {/* Insights & Recommendations */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                    <div className="flex gap-2">
                        <LightBlueIcons className="w-6 h-6 text-blue-500" />
                        <Typogaphy className="text-lg font-semibold text-gray-800 mb-4">
                            {t('services.analytics.key_insights')}
                        </Typogaphy>
                    </div>
                    <div className="space-y-3">
                        {summary.insights.map((insight, index) => (
                            <div
                                key={index}
                                className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg"
                            >
                                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-xs font-bold text-blue-600">
                                        {index + 1}
                                    </span>
                                </div>
                                <Typogaphy className="text-sm text-gray-700 leading-relaxed">
                                    {insight}
                                </Typogaphy>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recommendations */}
                <div>
                    <div className="flex gap-2">
                        <TargetIcons className="w-6 h-6 text-red-500" />
                        <Typogaphy className="text-lg font-semibold text-gray-800 mb-4">
                            {t('services.analytics.recommendations')}
                        </Typogaphy>
                    </div>
                    <div className="space-y-3">
                        {summary.recommendations.map(
                            (recommendation, index) => (
                                <div
                                    key={index}
                                    className="flex items-start gap-3 p-3 bg-green-50 rounded-lg"
                                >
                                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <span className="text-xs font-bold text-green-600">
                                            {index + 1}
                                        </span>
                                    </div>
                                    <Typogaphy className="text-sm text-gray-700 leading-relaxed">
                                        {recommendation}
                                    </Typogaphy>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div>
                <Typogaphy className="text-sm text-gray-600 text-center">
                    {t(
                        'services.analytics.analysis_generated_automatically_based_on'
                    )}{' '}
                    {config.timePeriod} {t('services.analytics.data_using')}{' '}
                    {config.aggregationMethod}{' '}
                    {t('services.analytics.aggregation_method')}.{' '}
                    {t('services.analytics.period')}: {config.startDate}{' '}
                    {t('services.analytics.to')} {config.endDate}
                </Typogaphy>
            </div>
        </Paper>
    )
}

export default AnalyticsSummaryPaper
